import React, { useRef, useState } from "react";
import { Dropdown, FormControl } from "react-bootstrap";
import { CountryPhoneWrapper } from "./CountryPhoneDropdown"; // You may rename this wrapper
import {
  CountryFlag,
  DropdownWrapper,
  OptionsDropdownContainer,
  StyledDropdownButton,
} from "../styles";
import { CustomLabel, FlexContainer } from "@mindmemobile/mm-shared-global";

export interface GenericItem {
  value: string;
  label: string;
  imageUrl?: string;
  dropdownItemClass?: string;
}

interface GenericDropdownProps {
  selectedItem?: GenericItem;
  error: boolean;
  errorMessage: string;
  onItemChange: (value: string) => void;
  className?: string;
  items: GenericItem[];
  placeholder?: string;
  dropdownItemClass?: string;
}

const GenericDropdown: React.FC<GenericDropdownProps> = ({
  selectedItem,
  error,
  errorMessage,
  onItemChange,
  className = "",
  items,
  placeholder = "Select Item",
  dropdownItemClass = "",
}) => {
  const [searchText, setSearchText] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);

  const filteredItems = items.filter((item) =>
    item.label.toLowerCase().includes(searchText.toLowerCase()),
  );

  // Focus the search box each time the menu opens (autoFocus only fires on the
  // initial mount, when the menu is still hidden). Also clear any stale search
  // text so the full list is shown on open.
  const handleToggle = (nextShow: boolean) => {
    if (nextShow) {
      setSearchText("");
      setTimeout(() => searchInputRef.current?.focus(), 0);
    }
  };

  const selectedItemIndex = items.findIndex(
    (item) => item.value === selectedItem?.value,
  );

  const imageLink =
    selectedItemIndex >= 0 ? items[selectedItemIndex]?.imageUrl : "";

  const handleSelect = (eventKey: string | null) => {
    if (eventKey) {
      onItemChange(eventKey);
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  return (
    <CountryPhoneWrapper className="generic-dropdown-wrapper">
      <OptionsDropdownContainer className={`generic-dpdn ${className}`}>
        <DropdownWrapper error={error.toString()}>
          <StyledDropdownButton
            id="dropdown-basic-button"
            title={
              selectedItem?.value ? (
                <FlexContainer
                  alignItems="center"
                  className={`styled-dropdown-flex ${dropdownItemClass} ${
                    selectedItem?.dropdownItemClass ?? ""
                  }`}
                >
                  {imageLink && (
                    <CountryFlag
                      className="item-image"
                      src={imageLink}
                      alt={selectedItem?.label}
                    />
                  )}
                  <CustomLabel
                    className="custom-label"
                    fontSize="large"
                    fontWeight="light"
                  >
                    {selectedItem?.label}
                  </CustomLabel>
                </FlexContainer>
              ) : (
                <CustomLabel
                  fontSize="large"
                  fontWeight="light"
                  className="dropdown-placeholder"
                >
                  {placeholder}
                </CustomLabel>
              )
            }
            onSelect={handleSelect}
            onToggle={handleToggle}
            variant="light"
            className="zoomed-arrow"
          >
            <FormControl
              ref={searchInputRef}
              placeholder={`Search ${placeholder}...`}
              onChange={handleSearch}
              value={searchText}
              className="dropdown-search-input"
              style={{
                outline: "none",
                boxShadow: "none",
                borderColor: "transparent",
              }}
            />
            {filteredItems.map((item) => (
              <Dropdown.Item
                key={item.value}
                eventKey={item.value}
                className={item.dropdownItemClass}
              >
                {item.imageUrl && (
                  <CountryFlag src={item.imageUrl} alt={item.label} />
                )}
                <span title={item.label}>{item.label}</span>
              </Dropdown.Item>
            ))}
          </StyledDropdownButton>
        </DropdownWrapper>
        {error && <p className="error-message">{errorMessage}</p>}
      </OptionsDropdownContainer>
    </CountryPhoneWrapper>
  );
};

export default GenericDropdown;
